import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Car, IdCard, Calendar, Users, Clock, Shield } from 'lucide-react';
import SEO from '../components/SEO';

const ForAuPairs = () => {
  const appUrl = process.env.REACT_APP_EXTERNAL_APP_URL || 'https://aupairshare.base44.app';

  const handleAppRedirect = () => {
    window.open(appUrl, '_blank');
  };

  const requirements = [
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Students',
      description: 'Currently registered at a South African university or college'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Qualified Child Carers',
      description: 'Adequately qualified child carers with relevant experience and credentials'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Senior Carers',
      description: 'Retired professionals such as ex-teachers, nurses, or experienced caregivers looking to continue making a difference'
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: 'Own Vehicle',
      description: 'Reliable personal vehicle for transportation'
    },
    {
      icon: <IdCard className="w-8 h-8" />,
      title: 'Valid Driver\'s Licence',
      description: 'Current South African driver\'s licence in good standing'
    }
  ];

  const benefits = [
    {
      icon: <Clock className="w-10 h-10" />,
      title: 'Work on Your Schedule',
      description: 'Accept bookings that fit around your classes and study time. You control your availability.'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Work with Multiple Families',
      description: 'Build relationships with various families and create a diverse income stream.'
    },
    {
      icon: <div className="w-10 h-10 bg-green-700 text-white rounded-full flex items-center justify-center text-2xl font-bold">R</div>,
      title: 'Reliable Income',
      description: 'Earn competitive rates with transparent payment processing through the app.'
    },
    {
      icon: <Calendar className="w-10 h-10" />,
      title: 'Flexible Commitment',
      description: 'No minimum hours. Take bookings when it suits you — whether weekly or occasionally.'
    }
  ];

  const onboardingSteps = [
    {
      step: '1',
      title: 'Apply via the App',
      description: 'Download the app and complete your profile with your details, documents, and availability.'
    },
    {
      step: '2',
      title: 'Verification Process',
      description: 'We verify your qualifications (student status or childcare credentials), license, and conduct background checks.'
    },
    {
      step: '3',
      title: 'Get Approved',
      description: 'Once approved, you can start accepting bookings immediately.'
    },
    {
      step: '4',
      title: 'Start Earning',
      description: 'Set your availability, accept bookings, and build your reputation with families.'
    }
  ];

  const whatYouWillDo = [
    'Safe transportation of children to/from school or activities',
    'Engage with children during care time',
    'Assist with homework or activities as needed',
    'Follow family-specific instructions and routines',
    'Maintain communication with parents through the app',
    'Provide attentive, responsible supervision'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="Become an Au Pair"
        description="Join Au Pair Share as an au pair. Students, qualified childcare professionals, and Senior Carers (retired teachers, nurses) welcome. Flexible hours in Johannesburg, Pretoria, Cape Town or Durban."
        keywords="become au pair, au pair jobs, childcare jobs South Africa, student jobs, nanny jobs Johannesburg, senior carer jobs, retired teacher jobs"
        path="/au-pairs"
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-50 opacity-50"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Earn while making a difference
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Join Au Pair Share and work flexible hours helping families — whether you're a student, qualified childcare professional, or a senior carer such as a retired teacher or nurse.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={handleAppRedirect}
                  className="bg-slate-700 hover:bg-slate-800 text-white px-8 py-6 text-lg"
                >
                  Apply via the App
                </Button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1666888736026-62d89f6cc7a3" 
                alt="Young au pair with baby"
                className="w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Who Can Apply
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              We are looking for responsible individuals with these essentials
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {requirements.map((req, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="bg-slate-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto text-slate-700">
                    {req.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{req.title}</h3>
                  <p className="text-slate-600">{req.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Why Join Au Pair Share
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Flexible work that fits around your schedule and commitments
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex gap-6">
                  <div className="bg-slate-100 p-4 rounded-lg h-fit text-slate-700">
                    {benefit.icon}
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-semibold text-slate-900">{benefit.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Do */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1489760176169-fd3d32805239" 
                  alt="Au pair caring for baby"
                  className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                />
              </div>
              <div className="space-y-6">
                <h2 className="text-4xl font-bold text-slate-900">
                  What You'll Do
                </h2>
                <ul className="space-y-4">
                  {whatYouWillDo.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="bg-slate-700 rounded-full p-1 mt-1">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Onboarding Works */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              How to Get Started
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Simple onboarding process — all through the app
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {onboardingSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 h-full">
                  <div className="bg-slate-700 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
                {index < onboardingSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-slate-300 z-0"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Support */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-slate-900 rounded-2xl p-12 text-white text-center">
            <Shield className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">
              You're Protected Too
            </h2>
            <p className="text-lg text-slate-300 mb-6">
              All families are verified, bookings are tracked, and you have support throughout your journey. We are committed to creating a safe, professional environment for both families and au pairs.
            </p>
            <Button 
              size="lg"
              onClick={handleAppRedirect}
              className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg"
            >
              Learn More in the App
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-4xl font-bold text-slate-900">
              Ready to Apply?
            </h2>
            <p className="text-xl text-slate-600">
              Join our community of trusted au pairs and start earning on your schedule.
            </p>
            <Button 
              size="lg"
              onClick={handleAppRedirect}
              className="bg-slate-700 hover:bg-slate-800 text-white px-8 py-6 text-lg mt-4"
            >
              Apply via the App
            </Button>
            <p className="text-sm text-slate-500 pt-2">
              All applications are processed through the app
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForAuPairs;
